import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  ACTIVE_STATUSES,
  PROJECT_STATUSES_OBJ
} from "../../../Projects/constants/project-statuses.ts"
import type { ProjectListItem } from "../../../Projects/types/types.ts"
import type { DashboardTileValue } from "../../redux/portfolio.types.ts"

export const ACTIVE_PROJECT_STATUS_LABELS: string[] = ACTIVE_STATUSES.map(
  (id) => PROJECT_STATUSES_OBJ[id]?.label
).filter(Boolean)

export interface SummaryStat {
  key: string
  labelKey: string
  fallback: string
  value: string
}

// Lead creation is a route (the wizard mounts as a child of the leads list);
// project creation is the `CreateProjectWizard` modal, which has no route of
// its own — `/projects/add` is the legacy form.
export type QuickAction = {
  key: string
  labelKey: string
  fallback: string
} & ({ to: string } | { opens: "projectWizard" })

export interface ProjectSnapshot {
  id: string
  uid: string | null
  name: string
  status: string
  progress: string
  budget?: string
  nextMilestone?: string
}

export interface RelativeTimeParts {
  unit: "now" | "minute" | "hour" | "day" | "week"
  count: number
}

// A summary tile renders a text-2xl numeral, so its no-data state is a dash
// rather than the "No data for this window" wording the KPI cards use.
export const NO_DATA_VALUE = "—"

export const readMetricValue = (
  allValues: Record<string, DashboardTileValue> | undefined,
  metric: string
): string => {
  const entry = allValues?.[metric]
  if (
    !entry ||
    entry.has_data === false ||
    entry.no_access ||
    entry.value == null
  ) {
    return NO_DATA_VALUE
  }
  return String(entry.value)
}

export const buildTopSummary = (input: {
  unreadCount: number
  activeProjects: string
  pendingApprovals: string
  tasksDue: string
}): SummaryStat[] => [
  {
    key: "notifications",
    labelKey: "mobileHome.summary.notifications",
    fallback: "Notifications",
    value: String(input.unreadCount)
  },
  {
    key: "activeProjects",
    labelKey: "mobileHome.summary.activeProjects",
    fallback: "Active projects",
    value: input.activeProjects
  },
  {
    key: "tasksDue",
    labelKey: "mobileHome.summary.tasksDue",
    fallback: "Tasks due",
    value: input.tasksDue
  },
  {
    key: "pendingApprovals",
    labelKey: "mobileHome.summary.pendingApprovals",
    fallback: "Approvals pending",
    value: input.pendingApprovals
  }
]

export const resolveQuickActions = (perms: {
  canCreateLead: boolean
  canCreateProject: boolean
}): QuickAction[] => {
  const actions: QuickAction[] = []
  if (perms.canCreateLead) {
    actions.push({
      key: "addLead",
      labelKey: "mobileHome.actions.addLead",
      fallback: "Add Lead",
      to: RoutesPaths.lead.add
    })
  }
  if (perms.canCreateProject) {
    actions.push({
      key: "createProject",
      labelKey: "mobileHome.actions.createProject",
      fallback: "Create Project",
      opens: "projectWizard"
    })
  }
  return actions
}

export interface ActivityItem {
  id: string
  title: string
  timestamp: string
  drill_to?: string
}

export const orderActivityItems = (
  items: ActivityItem[] = []
): ActivityItem[] =>
  [...items].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  )

const DRILL_SEGMENT = "(?!\\.{1,2}(?:/|$))[A-Za-z0-9._-]+"
const DRILL_PATH_PATTERN = new RegExp(
  `^/(leads|projects)/(${DRILL_SEGMENT})((?:/${DRILL_SEGMENT})*)/?$`
)

export const drillToPath = (
  drillTo: string | undefined | null
): string | null => {
  if (!drillTo) return null
  const match = DRILL_PATH_PATTERN.exec(drillTo.trim())
  if (!match) return null
  return `/${match[1]}/${match[2]}${match[3]}`
}

export const drillToEntityId = (
  drillTo: string | undefined | null
): string | null => {
  if (!drillTo) return null
  const match = DRILL_PATH_PATTERN.exec(drillTo.trim())
  return match ? match[2] : null
}

const firstStringField = (
  row: Record<string, unknown>,
  keys: string[]
): string | undefined => {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === "string" && value.trim()) return value
    if (typeof value === "number") return String(value)
  }
  return undefined
}

export const extractNextMilestone = (
  rows: Array<Record<string, unknown>> = [],
  project: { id: string; uid: string | null; name: string }
): string | undefined => {
  const match = rows.find((row) => {
    const rowProjectId =
      firstStringField(row, ["project_id", "projectId"]) ??
      drillToEntityId(firstStringField(row, ["drill_to"]))
    const rowProjectName = firstStringField(row, [
      "project_name",
      "projectName",
      "project"
    ])
    return (
      (!!rowProjectId && rowProjectId === project.id) ||
      (!!project.uid && rowProjectName === project.uid) ||
      (!!rowProjectName && rowProjectName === project.name)
    )
  })
  if (!match) return undefined
  return firstStringField(match, [
    "phase",
    "milestone",
    "milestone_name",
    "name",
    "title"
  ])
}

export const buildProjectSnapshots = (
  projects: ProjectListItem[] = [],
  milestoneRows: Array<Record<string, unknown>> = [],
  limit = 8
): ProjectSnapshot[] =>
  projects
    .filter((project) =>
      ACTIVE_PROJECT_STATUS_LABELS.includes(
        String(project?.project_status ?? "")
      )
    )
    .slice(0, limit)
    .map((project) => {
      const name = project.lead_name || ""
      return {
        id: String(project.id),
        uid: project.uid ?? null,
        name,
        status: String(project.project_status ?? ""),
        progress: project.progress || "0%",
        budget: project.total_budget || undefined,
        nextMilestone: extractNextMilestone(milestoneRows, {
          id: String(project.id),
          uid: project.uid ?? null,
          name
        })
      }
    })

export const getRelativeTimeParts = (
  iso: string,
  nowMs: number = Date.now()
): RelativeTimeParts => {
  const diffMs = Math.max(0, nowMs - new Date(iso).getTime())
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return { unit: "now", count: 0 }
  if (minutes < 60) return { unit: "minute", count: minutes }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return { unit: "hour", count: hours }
  const days = Math.floor(hours / 24)
  if (days < 7) return { unit: "day", count: days }
  return { unit: "week", count: Math.floor(days / 7) }
}
